import { and, eq, gt, lt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import { secondsToHours, sumNetWorkSeconds } from '@/lib/dates'
import { useStore } from '@/lib/pocketbase'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface UsePendingEntriesOptions {
    status?: string
    userId?: string
    startDate?: string
    endDate?: string
    enabled?: boolean
}

export function usePendingEntries({
    status,
    userId,
    startDate,
    endDate,
    enabled = true,
}: UsePendingEntriesOptions = {}) {
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()

    const [
        timeEntriesCollection,
        usersCollection,
        jobsCollection,
        locationsCollection,
        orgsCollection,
        customersCollection,
    ] = useStore('time_entries', 'users', 'jobs', 'locations', 'orgs', 'customers')

    const { data: orgData } = useLiveQuery(
        q =>
            orgSlug
                ? q
                      .from({ org: orgsCollection })
                      .where(({ org }) => eq(org.slug, orgSlug))
                      .select(({ org }) => ({ id: org.id }))
                : undefined,
        [orgSlug]
    )
    const orgId = orgData?.[0]?.id

    const { isLoading, data: rawEntries = [] } = useLiveQuery(
        q =>
            orgId && enabled
                ? q
                      .from({ entry: timeEntriesCollection })
                      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: multiple filter conditions
                      .where(({ entry }) => {
                          let condition = eq(entry.org, orgId)
                          if (status) condition = and(condition, eq(entry.status, status))
                          if (userId) condition = and(condition, eq(entry.user, userId))
                          if (startDate) condition = and(condition, gt(entry.clock_in, startDate))
                          if (endDate) condition = and(condition, lt(entry.clock_in, endDate))
                          return condition
                      })
                      .orderBy(({ entry }) => entry.clock_in, 'desc')
                : undefined,
        [orgId, userId, startDate, endDate, status, enabled]
    )

    const entries = useMemo(() => {
        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: nullable FK lookups with fallbacks
        const lookupExpand = (entry: TimeEntriesExpanded) => {
            const job = entry.job ? jobsCollection.get(entry.job) : undefined
            return {
                ...entry.expand,
                user: usersCollection.get(entry.user) ?? entry.expand?.user,
                job: job ?? entry.expand?.job,
                location: entry.location
                    ? (locationsCollection.get(entry.location) ?? entry.expand?.location)
                    : entry.expand?.location,
                approved_by: entry.approved_by
                    ? (usersCollection.get(entry.approved_by) ?? entry.expand?.approved_by)
                    : entry.expand?.approved_by,
                customer: job?.customer
                    ? (customersCollection.get(job.customer) ?? entry.expand?.customer)
                    : entry.expand?.customer,
            }
        }
        return rawEntries.map(rawEntry => {
            const entry = rawEntry as TimeEntriesExpanded
            return { ...entry, expand: lookupExpand(entry) }
        })
    }, [rawEntries, usersCollection, jobsCollection, locationsCollection, customersCollection])

    const totalHours = secondsToHours(sumNetWorkSeconds(entries))

    const entryCount = entries.length

    const entriesByUser = entries.reduce(
        (acc, entry) => {
            const userId = entry.user
            if (!acc[userId]) {
                acc[userId] = []
            }
            acc[userId].push(entry)
            return acc
        },
        {} as Record<string, TimeEntriesExpanded[]>
    )

    return {
        entries,
        entriesByUser,
        totalHours,
        entryCount,
        isLoading,
    }
}
